/**
 * PrimaryQuoteTypePicker · Appraisal Booked · Action 20
 * ─────────────────────────────────────────────────────
 * Multi-select checkboxes for the primary appraisal work type(s).
 *
 * • Shortlist source: property.preferred_quote_types[] (captured in
 *   Client Data · Action 5). If empty, falls back to the canonical taxonomy.
 * • Operator picks ONE OR MORE as the primary type(s) for this appraisal.
 * • "+ Add another type" pill exposes the full canonical taxonomy for
 *   picks that weren't in the intake shortlist. Adding a type also
 *   patches property.preferred_quote_types so the shortlist stays a
 *   truthful record of everything the client has expressed interest in.
 * • First pick mirrors to property.preferred_quote_type (singular) —
 *   the legacy field feeds the Property Detail sidebar card.
 *
 * Writes on Next:
 *   property.preferred_quote_types_primary = [...picks]
 *   property.preferred_quote_type          = picks[0]
 *   property.preferred_quote_types         = union(shortlist, picks)
 * Runner captured_data patch (via write_to):
 *   [write_to]: picks  (list of strings)
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Check, Plus, X, Pencil } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// v10.26 (Feb 2026 · UAT A999682) — Static class table. Tailwind's JIT
// compiler CANNOT see dynamically-constructed class names like
// `bg-${c}-500` — those classes are purged from the CSS bundle unless
// they exist as literal strings somewhere in the source. Every colour
// except `fuchsia` (Re-Coat) happens to be used elsewhere in the app,
// so the old dynamic template rendered Re-Coat as white-on-white and
// the word became invisible. Explicit literal classes fix this
// permanently and stop future colour additions from silently breaking.
const PILL_STYLES = {
  'Re-Roof':          { active: 'bg-sky-500 text-white border-sky-500 shadow',           idle: 'bg-white text-sky-700 border-sky-200 hover:bg-sky-50' },
  'New Roof':         { active: 'bg-indigo-500 text-white border-indigo-500 shadow',     idle: 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50' },
  'Maintenance':      { active: 'bg-emerald-500 text-white border-emerald-500 shadow',   idle: 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50' },
  'Remedial Repairs': { active: 'bg-amber-500 text-white border-amber-500 shadow',       idle: 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50' },
  'Re-Coat':          { active: 'bg-fuchsia-500 text-white border-fuchsia-500 shadow',   idle: 'bg-white text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50' },
  'Repairs':          { active: 'bg-rose-500 text-white border-rose-500 shadow',         idle: 'bg-white text-rose-700 border-rose-200 hover:bg-rose-50' },
  'Restoration':      { active: 'bg-purple-500 text-white border-purple-500 shadow',     idle: 'bg-white text-purple-700 border-purple-200 hover:bg-purple-50' },
  'Guttering':        { active: 'bg-teal-500 text-white border-teal-500 shadow',         idle: 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50' },
};
const FALLBACK_PILL = { active: 'bg-slate-500 text-white border-slate-500 shadow', idle: 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50' };
const pillClass = (name, active) => {
  const style = PILL_STYLES[name] || FALLBACK_PILL;
  return active ? style.active : style.idle;
};

export default function PrimaryQuoteTypePicker({
  action, capturedData, value, onChange, onNext, onBack, canBack,
  statusKey, taskId, onTemplateChanged,
}) {
  const params = action.params || {};
  const allowExtra = params.allow_add_extra !== false;

  const propertyId = capturedData?.property_id;
  const propRec = capturedData?.property_record || {};
  const shortlist = useMemo(
    () => Array.isArray(propRec.preferred_quote_types) ? propRec.preferred_quote_types : [],
    [propRec],
  );

  const initialPicks = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(propRec.preferred_quote_types_primary)) return propRec.preferred_quote_types_primary;
    // iter303aj-fix8 — Auto-pick the intake shortlist as the default so
    // operators just tap Next to confirm rather than re-picking. They can
    // still untick to remove any (e.g. client said Re-Roof AND Restoration
    // at intake but the salesman thinks only Re-Roof is primary this quote).
    if (Array.isArray(propRec.preferred_quote_types)) return propRec.preferred_quote_types;
    return [];
  }, [value, propRec]);

  const [picks, setPicks] = useState(initialPicks);
  const [canonical, setCanonical] = useState([]);
  const [extraOpen, setExtraOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [promptDraft, setPromptDraft] = useState(params.prompt || '');
  const [editingPrompt, setEditingPrompt] = useState(false);

  useEffect(() => { setPicks(initialPicks); }, [action.id, initialPicks]);
  useEffect(() => { setPromptDraft(params.prompt || ''); }, [params.prompt, action.id]);

  // Fetch canonical list on mount (for "+ Add another type")
  useEffect(() => {
    let alive = true;
    api.get('/dropdowns/preferred_quote_types').then(r => {
      if (alive) setCanonical(Array.isArray(r.data) ? r.data : (r.data?.options || []));
    }).catch(() => { if (alive) setCanonical(['Re-Roof', 'New Roof', 'Maintenance', 'Remedial Repairs', 'Re-Coat', 'Repairs', 'Restoration', 'Guttering']); });
    return () => { alive = false; };
  }, []);

  // Effective option list: shortlist first, then any picks the operator
  // added via "+ Add another type". Never renders duplicates.
  const options = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of shortlist) { if (s && !seen.has(s)) { seen.add(s); out.push(s); } }
    for (const p of picks)     { if (p && !seen.has(p)) { seen.add(p); out.push(p); } }
    return out;
  }, [shortlist, picks]);

  const notPicked = canonical.filter(c => !options.includes(c));

  const toggle = (name) => {
    setPicks(prev => {
      const next = prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name];
      onChange(next);
      return next;
    });
  };

  const addExtra = (name) => {
    setPicks(prev => {
      if (prev.includes(name)) return prev;
      const next = [...prev, name];
      onChange(next);
      return next;
    });
    setExtraOpen(false);
  };

  const canEdit = !!(statusKey && taskId && action?.id);
  const savePrompt = async () => {
    setSaving(true); setErr('');
    try {
      await api.patch(
        `/status-workflows/workflow/${statusKey}/tasks/${taskId}/actions/${action.id}`,
        { params: { prompt: promptDraft.trim() } },
      );
      if (onTemplateChanged) await onTemplateChanged();
      setEditingPrompt(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const commit = async () => {
    if (picks.length === 0) return;
    setSaving(true); setErr('');
    try {
      // Persist to property first so the Property Detail page reflects the pick
      if (propertyId) {
        const mergedShortlist = Array.from(new Set([...shortlist, ...picks]));
        await api.patch(`/properties/${propertyId}`, {
          preferred_quote_types_primary: picks,
          preferred_quote_type: picks[0],
          preferred_quote_types: mergedShortlist,
        });
      }
      // Also mirror to the appraisal itself so downstream renderers /
      // reports can find the primary type(s) without walking the property.
      const appraisalId = capturedData?.appraisal_id;
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, {
          primary_quote_types: picks,
        });
      }
      onChange(picks);
      onNext(picks);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="wf-primary-quote-type-picker">
      <div className="bg-gradient-to-br from-fuchsia-50 to-white border-l-4 border-fuchsia-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-600 font-semibold mb-2 flex items-center gap-2">
          <span>🎯 Primary Work Type</span>
          {canEdit && !editingPrompt && (
            <button onClick={() => setEditingPrompt(true)} data-testid="wf-primary-prompt-edit"
              className="text-fuchsia-500 hover:text-fuchsia-700 flex items-center gap-1">
              <Pencil size={10} /> edit
            </button>
          )}
        </div>
        {editingPrompt ? (
          <div>
            <textarea rows={3} value={promptDraft} onChange={e => setPromptDraft(e.target.value)}
              data-testid="wf-primary-prompt-input"
              className="w-full text-base text-slate-800 leading-relaxed border border-fuchsia-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-fuchsia-300" />
            <div className="mt-2 flex items-center gap-2">
              <button onClick={savePrompt} disabled={saving || !promptDraft.trim()}
                className="px-3 py-1 bg-fuchsia-600 text-white text-xs rounded hover:bg-fuchsia-700 disabled:opacity-40 flex items-center gap-1">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
              </button>
              <button onClick={() => { setEditingPrompt(false); setPromptDraft(params.prompt || ''); }}
                className="px-3 py-1 text-slate-500 text-xs hover:text-slate-700 flex items-center gap-1">
                <X size={12} /> Cancel
              </button>
              {err && <span className="text-xs text-rose-600">{err}</span>}
            </div>
          </div>
        ) : (
          <div className="text-lg text-slate-800 leading-relaxed" data-testid="wf-primary-prompt">
            {params.prompt || 'Which trade(s) is the client primarily after?'}
          </div>
        )}
      </div>

      {shortlist.length === 0 && (
        <div className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2" data-testid="wf-primary-empty-shortlist">
          No shortlist was captured in Client Data — the operator will need to add types from scratch via <b>+ Add another type</b> below.
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-4" data-testid="wf-primary-options">
        {options.map(name => {
          const active = picks.includes(name);
          return (
            <button key={name} type="button" onClick={() => toggle(name)}
              data-testid={`wf-primary-opt-${name}`}
              className={`px-4 py-3 border-2 rounded font-semibold transition text-left ${pillClass(name, active)}`}>
              <span className="inline-flex items-center gap-2">
                {active && <Check size={14} />}
                <span>{name}</span>
              </span>
              {shortlist.includes(name) && (
                <span className={`ml-2 text-[10px] uppercase tracking-widest ${active ? 'text-white/70' : 'text-slate-400'}`}>
                  from intake
                </span>
              )}
            </button>
          );
        })}
      </div>

      {allowExtra && (
        <div className="mb-6">
          {!extraOpen ? (
            <button type="button" onClick={() => setExtraOpen(true)}
              data-testid="wf-primary-add-extra"
              className="px-3 py-1.5 text-xs border-2 border-dashed border-slate-300 rounded text-slate-500 hover:border-fuchsia-300 hover:text-fuchsia-700 flex items-center gap-1">
              <Plus size={12} /> Add another type
            </button>
          ) : (
            <div className="p-3 border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/40 rounded">
              <div className="text-xs text-slate-600 mb-2">Pick a type that wasn&apos;t in the intake shortlist:</div>
              <div className="flex items-center gap-2 flex-wrap" data-testid="wf-primary-extra-list">
                {notPicked.length === 0 && <span className="text-xs text-slate-400 italic">All canonical types already listed.</span>}
                {notPicked.map(name => (
                  <button key={name} type="button" onClick={() => addExtra(name)}
                    data-testid={`wf-primary-extra-${name}`}
                    className="px-3 py-1 text-xs bg-white border border-fuchsia-200 rounded hover:bg-fuchsia-100 text-fuchsia-800">
                    + {name}
                  </button>
                ))}
                <button type="button" onClick={() => setExtraOpen(false)}
                  className="ml-auto text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  <X size={12} /> Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {picks.length > 0 && (
        <div className="mb-4 text-xs text-slate-500" data-testid="wf-primary-summary">
          <b>Primary pick:</b> {picks[0]}{picks.length > 1 && <> · <b>Also flagged as primary:</b> {picks.slice(1).join(', ')}</>}
        </div>
      )}
      {params.write_to && (
        <div className="text-xs text-slate-400 mb-4">
          <span className="font-semibold text-slate-500">Writes to:</span> <code>{params.write_to}</code> + <code>property.preferred_quote_type(s)_primary</code>
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={commit} disabled={saving || picks.length === 0}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 flex items-center gap-2">
          {saving && <Loader2 size={14} className="animate-spin" />} Next →
        </button>
      </div>
    </div>
  );
}
